import { Box, Breadcrumbs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppTooltip from '@tenant/core/AppTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntlMessages from '@tenant/utility/IntlMessages';
import { ROUTE_STRING } from 'constants/RouteString';
import { Formik } from 'formik';
import validationSchema from '../../redux/slices/appPackage/constants/validationSchema';
import useAddAppPackage from 'redux/slices/appPackage/hooks/useAddAppPackage';
import AppPackageAddForm from './components/Common/AppPackageAddForm';
import AddStepper from './components/Add/AddStepper';
import AppsContainer from '@tenant/core/AppsContainer';

const steps = ['Information', 'Components', 'Confirmation'];
const MAX_STEP = steps.length - 1;
const MIN_STEP = 0;

const Add = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const onClickBackButton = () => {
    navigate(ROUTE_STRING.APP_PACKAGE.LIST);
  };
  const addItem = useAddAppPackage();
  const [isValidAppContainer, setIsValidAppContainer] = useState(false);

  const goNextStep = () => {
    setStep((s) => (s >= MAX_STEP ? MAX_STEP : s + 1));
  };

  const goPreviousStep = () => {
    setStep((s) => (s <= MIN_STEP ? MIN_STEP : s - 1));
  };

  const handleSubmit = (data) => {
    if (step < MAX_STEP) {
      goNextStep();
      return;
    }

    if (!isValidAppContainer) {
      return;
    }

    addItem.mutate(
      {
        ...data,
        AppComponents: data?.AppComponents?.map((e) => ({
          Name: e.Name,
          ImageName: e.ImageName,
        })),
        AppModel: undefined,
        AppId: data.AppModel?.Id,
      },
      {
        onSuccess(result) {
          if (!result?.data?.Id) return;
          navigate(
            ROUTE_STRING.APP_PACKAGE.VIEW.replace(':id', result?.data?.Id),
          );
        },
      },
    );
  };

  const handleChangeIsValidAppContainer = (newValue) => {
    setIsValidAppContainer(newValue);
  };

  return (
    <AppsContainer
      fullView
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Library</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.APP_PACKAGE.LIST}>App Packages</Link>
          </Typography>
          <Typography color='text.primary'>Add</Typography>
        </Breadcrumbs>
      }
    >
      <AppsHeader>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              gap: 4,
            }}
            component='span'
            mr={{ xs: 2, sm: 4 }}
          >
            <AppTooltip title={<IntlMessages id='common.back' />}>
              <ArrowBackIcon
                sx={{
                  color: 'text.secondary',
                }}
                onClick={onClickBackButton}
              />
            </AppTooltip>
          </Box>

          <Box sx={{ width: '100%' }}>
            <AddStepper steps={steps} step={step} />
          </Box>
        </Box>
      </AppsHeader>

      <Box p={4}>
        <Formik
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={{}}
          onSubmit={handleSubmit}
          validationSchema={validationSchema[step]}
        >
          {({ submitForm, values, setFieldValue, errors, validateField }) => (
            <AppPackageAddForm
              submitForm={submitForm}
              validateField={validateField}
              setForm={setFieldValue}
              onCancel={onClickBackButton}
              onPrevious={goPreviousStep}
              handleChangeIsValidAppContainer={handleChangeIsValidAppContainer}
              values={values}
              errors={errors}
              isValidAppContainer={isValidAppContainer}
              step={step}
              loading={addItem.isPending}
              maxStep={MAX_STEP}
            />
          )}
        </Formik>
      </Box>
    </AppsContainer>
  );
};

export default Add;
